import React from 'react';
import { AlertCircle, ChevronDown, RefreshCw, Trash2, Upload } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { TenderQuoteDetailDrawer } from './TenderQuoteDetailDrawer';
import {
  SUPPLIER_QUOTE_STATUS_LABELS,
  type SupplierQuote,
  type TenderDocument,
} from '../types';

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const dateFmt = new Intl.DateTimeFormat('de-DE');

interface Props {
  ausschreibungId: string;
  quotes: SupplierQuote[];
  documents: TenderDocument[];
  loading: boolean;
  /** Angebote neu laden (nach Aktionen oder Polling). */
  onRefresh: () => void | Promise<void>;
}

function statusClass(status: SupplierQuote['status']): string {
  switch (status) {
    case 'ready':
      return 'bg-emerald-50 text-emerald-700';
    case 'failed':
      return 'bg-red-50 text-red-700';
    case 'extracting':
      return 'bg-blue-50 text-blue-700';
    default:
      return 'bg-app-surface-muted text-app-text-subtle';
  }
}

/**
 * Lieferantenangebote der Akte: Liste mit Status der Extraktion, UGL-Import, Neu-Lesen,
 * Löschen; Zeilen im Drawer. Dokumente, die die Klassifizierung nicht als Angebot erkannt
 * hat, lassen sich hier als Angebot nachmelden.
 */
export const TenderQuotesPanel: React.FC<Props> = ({ ausschreibungId, quotes, documents, loading, onRefresh }) => {
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [openQuoteId, setOpenQuoteId] = React.useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<SupplierQuote | null>(null);
  const [uglUploading, setUglUploading] = React.useState(false);
  const [open, setOpen] = React.useState(true);
  const uglInputRef = React.useRef<HTMLInputElement | null>(null);

  const quoteDocIds = React.useMemo(() => new Set(quotes.map((q) => q.source_document_id).filter(Boolean)), [quotes]);
  const candidateDocs = React.useMemo(
    () =>
      documents.filter(
        (d) =>
          !quoteDocIds.has(d.id) &&
          d.doc_kind !== 'gaeb' &&
          (/\.pdf$/i.test(d.filename) || String(d.mime_type ?? '').startsWith('image/')),
      ),
    [documents, quoteDocIds],
  );

  const call = async (quoteId: string, path: string, init?: RequestInit) => {
    setBusyId(quoteId);
    setError(null);
    try {
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}${path}`, {
        credentials: 'include',
        ...init,
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body.error || 'Aktion fehlgeschlagen.');
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const markDocAsQuote = async (docId: string) => {
    await call(docId, `/documents/${encodeURIComponent(docId)}/mark-quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
  };

  const uploadUgl = async (file: File) => {
    setUglUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/quotes/import-ugl`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body.error || 'UGL-Import fehlgeschlagen.');
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUglUploading(false);
    }
  };

  return (
    <section className="rounded-lg border border-app-border bg-app-surface shadow-sm">
      <div className={`flex items-center justify-between gap-3 px-4 py-3 ${open ? 'border-b border-app-border' : ''}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 min-w-0 text-left"
          aria-expanded={open}
        >
          <ChevronDown className={`w-4 h-4 text-app-text-subtle shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
          <h2 className="text-base font-semibold text-app-text truncate">Lieferantenangebote</h2>
          <span className="text-sm text-app-text-subtle">· {quotes.length}</span>
          {loading && <LoadingSpinner size="xs" />}
        </button>
        <div className="flex items-center gap-2">
          <input
            ref={uglInputRef}
            type="file"
            accept=".ugl,.txt,.dat"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadUgl(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => uglInputRef.current?.click()}
            disabled={uglUploading}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text disabled:opacity-50 transition"
            title="UGL-Angebot des Großhändlers als Lieferantenangebot einlesen"
          >
            {uglUploading ? <LoadingSpinner size="xs" /> : <Upload className="w-4 h-4" />}
            UGL importieren
          </button>
        </div>
      </div>

      <div className={open ? '' : 'hidden'}>
        {error && (
          <div className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 inline-flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {quotes.length === 0 ? (
          <p className="px-4 py-3 text-sm text-app-text-muted">
            Noch keine Lieferantenangebote. PDF-Angebote der Großhändler einfach als Dokument hochladen; sie werden
            erkannt und gelesen.
          </p>
        ) : (
          <ul className="py-1">
            {quotes.map((q) => (
              <li key={q.id} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-app-hover/60 transition-colors">
                <button
                  type="button"
                  onClick={() => setOpenQuoteId(q.id)}
                  className="flex-1 min-w-0 text-left flex items-center gap-2 group"
                  title="Zeilen anzeigen"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-app-text group-hover:text-pipedrive-blue">
                      {q.supplier_name || '(Lieferant unbekannt)'}
                      {q.quote_number ? <span className="text-app-text-subtle font-normal"> · {q.quote_number}</span> : null}
                    </span>
                    <span className="block truncate text-sm text-app-text-subtle">
                      {q.source_filename ?? (q.source_kind === 'ugl' ? 'UGL' : '')}
                      {q.quote_date ? ` · ${dateFmt.format(new Date(q.quote_date))}` : ''}
                      {q.status === 'ready' ? ` · ${q.item_count} Zeilen` : ''}
                    </span>
                  </span>
                </button>
                {q.net_total != null && (
                  <span className="tabular-nums text-app-text whitespace-nowrap">{euro.format(q.net_total)}</span>
                )}
                <span
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold ${statusClass(q.status)}`}
                  title={q.status_error ?? q.header_notes ?? undefined}
                >
                  {q.status === 'extracting' && <LoadingSpinner size="xs" />}
                  {SUPPLIER_QUOTE_STATUS_LABELS[q.status]}
                </span>
                {q.source_kind === 'pdf' && q.status !== 'extracting' && (
                  <button
                    type="button"
                    onClick={() =>
                      void call(q.id, `/quotes/${encodeURIComponent(q.id)}/reextract`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ all_pages: q.status === 'ready' && q.pages_skipped > 0 }),
                      })
                    }
                    disabled={busyId === q.id}
                    className="p-1 rounded hover:bg-app-hover text-slate-400 hover:text-app-text transition"
                    title={
                      q.status === 'ready' && q.pages_skipped > 0
                        ? `Erneut lesen, diesmal alle Seiten (${q.pages_skipped} übersprungen)`
                        : 'Erneut lesen'
                    }
                  >
                    {busyId === q.id ? <LoadingSpinner size="xs" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setPendingDelete(q)}
                  className="p-1 rounded hover:bg-app-hover text-slate-400 hover:text-red-600 transition"
                  title="Angebot entfernen (Dokument bleibt)"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {candidateDocs.length > 0 && (
          <div className="px-4 py-3 border-t border-app-border">
            <p className="text-sm text-app-text-subtle mb-1">
              Nicht als Angebot erkannt. Falls doch ein Lieferantenangebot:
            </p>
            <ul className="flex flex-wrap gap-1.5">
              {candidateDocs.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => void markDocAsQuote(d.id)}
                    disabled={busyId === d.id}
                    className="inline-flex items-center gap-1 rounded border border-app-border bg-app-surface px-2 py-1 text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                    title={d.doc_label ? `Erkannt als: ${d.doc_label}` : 'Als Lieferantenangebot lesen'}
                  >
                    {busyId === d.id && <LoadingSpinner size="xs" />}
                    <span className="max-w-[16rem] truncate">{d.filename}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <TenderQuoteDetailDrawer
        ausschreibungId={ausschreibungId}
        quoteId={openQuoteId}
        onClose={() => setOpenQuoteId(null)}
        onChanged={onRefresh}
      />

      <AppConfirmDialog
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        title="Angebot entfernen?"
        description={
          pendingDelete ? (
            <>
              Das Angebot <span className="font-medium text-app-text">{pendingDelete.supplier_name || pendingDelete.source_filename}</span>{' '}
              und seine Zeilen werden entfernt. Das hochgeladene Dokument bleibt in der Akte.
            </>
          ) : null
        }
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={busyId === pendingDelete?.id}
        onConfirm={async () => {
          if (!pendingDelete) return;
          const id = pendingDelete.id;
          setPendingDelete(null);
          await call(id, `/quotes/${encodeURIComponent(id)}`, { method: 'DELETE' });
        }}
      />
    </section>
  );
};
